import React from "react"
import CloseModal from "../CloseModal"
import DragDropArea from "../DragDropArea"
import Loader from "../ui/loader"
import useLoading from "../../hooks/useLoading"
import { Button } from "../ui/button"
import { CoachPrivateProfileContext } from "../../pages/manage-business/profile/context/coachPrivateProfileContext"
import { CommonFunctionCtx } from "../../context/commonFunctionContext"
import { ProfileRequests } from "../../api/app.service"
import { SvgTrash, SvgUploadImage, SvgCrop } from "../icons"
import { useToast } from "../../hooks/useToast"
import ReactCrop, {
  centerCrop,
  type Crop,
  makeAspectCrop,
} from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"
export default function EditBannerImage() {
  const {
    setEditBannerImage,
    previewImgUrl,
    setPreviewImgUrl,
    coachPublicProfileData,
    getCoachPrivateProfile,
  } = React.useContext(CoachPrivateProfileContext)
  const { setPopupNotification } = React.useContext(CommonFunctionCtx)
  const { startLoading, stopLoading } = useLoading()
  const { toast } = useToast()
  const [loadingSave, setLoadingSave] = React.useState<boolean>(false)
  const [loadingDelete, setLoadingDelete] = React.useState<boolean>(false)
  const [bannerImgFile, setBannerImgFile] = React.useState<File>()
  const [awaitNewImage, setAwaitNewImage] = React.useState<boolean>(false)
  const [imageProperties, setImageProperties] = React.useState<any>(null)
  const [showCrop, setShowCrop] = React.useState<boolean>(false)
  const [crop, setCrop] = React.useState<Crop>()
  const [imgRef, setImgRef] = React.useState<HTMLImageElement | null>(null)

  const validateFile = (imageFile: File) => {
    if (
      (imageFile.type === "image/png" ||
        imageFile.type === "image/jpg" ||
        imageFile.type === "image/jpeg") &&
      imageFile.size <= 5000000 &&
      imageFile.size > 0
    ) {
      return true
    } else if (
      imageFile.type === "video/mp4" &&
      imageFile.size <= 5000000 &&
      imageFile.size > 0
    ) {
      return true
    } else {
      return false
    }
  }

  const getCroppedImg = (
    image: HTMLImageElement,
    crop: Crop,
    fileName: string
  ): Promise<{ base64: string; type: string }> => {
    const canvas = document.createElement("canvas")
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    // Convert percentage crop to pixel values
    const cropX = (crop.x! / 100) * image.width * scaleX
    const cropY = (crop.y! / 100) * image.height * scaleY
    const cropWidth = (crop.width! / 100) * image.width * scaleX
    const cropHeight = (crop.height! / 100) * image.height * scaleY

    canvas.width = cropWidth
    canvas.height = cropHeight

    const ctx = canvas.getContext("2d")
    if (!ctx) {
      throw new Error("No 2d context")
    }

    ctx.imageSmoothingQuality = "high"

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    )

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"))
            return
          }
          const reader = new FileReader()
          reader.readAsDataURL(blob)
          reader.onload = function () {
            const string = (reader.result as string).split("\n").map((data) => {
              return data.split(",")
            })
            const base64 = string[0][1]
            const type = blob.type.split("/")[1]
            resolve({ base64, type })
          }
          reader.onerror = reject
        },
        "image/jpeg",
        0.95
      )
    })
  }

  const saveBannerImage = () => {
    startLoading()
    setLoadingSave(true)
    ProfileRequests.editBannerImage({
      coach_public_profile_id: coachPublicProfileData?.coach_public_profile_id,
      base_64_string: imageProperties.base64,
      file_extension: imageProperties.base64extension,
    })
      .then(() => {
        getCoachPrivateProfile().then(() => {
          setLoadingSave(false)
          toast.success("Uploaded banner image")
          setBannerImgFile(undefined)
          setAwaitNewImage(false)
          setEditBannerImage(false)
          setShowCrop(false)
          stopLoading()
          getCoachPrivateProfile()
        })
      })
      .catch((ex: any) => {
        console.error(ex)
        setBannerImgFile(undefined)
        setPreviewImgUrl("")
        setImageProperties(null)
        toast.error(ex.response.data.message)
        stopLoading()
      })
  }

  const handleCropComplete = async () => {
    if (!imgRef || !crop || !bannerImgFile) return

    try {
      const { base64, type } = await getCroppedImg(
        imgRef,
        crop,
        bannerImgFile.name
      )
      setImageProperties({
        base64: base64,
        base64extension: type,
      })
      // Update preview with cropped image
      const croppedDataUrl = `data:image/${type};base64,${base64}`
      setPreviewImgUrl(croppedDataUrl)
      setShowCrop(false)
      toast.success("Image cropped successfully")
    } catch (error) {
      console.error("Error cropping image:", error)
      toast.error("Failed to crop image")
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget

    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        3.5 / 1,
        width,
        height
      ),
      width,
      height
    )

    setCrop(crop)
  }

  const deleteBannerImage = () => {
    startLoading()
    setLoadingDelete(true)
    ProfileRequests.deleteBannerImage({
      coach_public_profile_id: coachPublicProfileData?.coach_public_profile_id,
    })
      .then(() => {
        setLoadingDelete(false)
        toast.success("Deleted banner image")
        setBannerImgFile(undefined)
        setAwaitNewImage(false)
        setEditBannerImage(false)
        stopLoading()
        getCoachPrivateProfile()
      })
      .catch((ex: any) => {
        console.error(ex)
        setBannerImgFile(undefined)
        setPreviewImgUrl("")
        setImageProperties(null)
        toast.error(ex.response.data.message)
        stopLoading()
      })
  }

  const setBannerImgDetails = () => {
    if (bannerImgFile) {
      if (validateFile(bannerImgFile)) {
        setPreviewImgUrl(URL.createObjectURL(bannerImgFile))
        let base64 = ""
        const type = bannerImgFile.type.split("/")[1]
        const reader = new FileReader()
        reader.readAsDataURL(bannerImgFile)
        reader.onload = function () {
          const string = (reader.result as string).split("\n").map((data) => {
            return data.split(",")
          })
          base64 = string[0][1]
          setImageProperties({
            base64: base64,
            base64extension: type,
          })
        }
        reader.onerror = function (error) {
          console.log("Error: ", error)
        }
        setAwaitNewImage(false)
      } else {
        setPopupNotification({
          show: true,
          title: "Bummer! Your file is too big.",
          message:
            "Give it another try with a file under 5 megabytes, and in one of the following formats: png, jpg, or jpeg.",
          callback: null,
        })
        setBannerImgFile(undefined)
        setAwaitNewImage(true)
        setImageProperties(null)
        setPreviewImgUrl("")
      }
    }
  }

  React.useEffect(() => {
    setBannerImgDetails()
  }, [bannerImgFile])

  return (
    <div className="zoee-backdrop-filter fixed left-0 top-0 z-[1009] flex h-screen w-screen items-center justify-center md:z-[801]">
      <div className="main-shadow fixed left-[50%] top-[60px] flex w-[800px] max-w-[90%] -translate-x-[50%] flex-col items-start rounded-[16px] bg-white px-[40px] pb-[40px] pt-[123px]">
        <CloseModal
          callback={() => {
            setEditBannerImage(false)
            setPreviewImgUrl("")
            setBannerImgFile(undefined)
            setImageProperties(null)
            setShowCrop(false)
          }}
          styling="absolute top-6 right-8"
        />
        <h2 className="absolute left-[32px] top-[26px] text-[18px] font-bold">
          Edit Header Image
        </h2>
        <div className="mx-auto flex flex-col items-center">
          <p className="text-graySlate mb-[8px] text-balance text-center text-sm font-bold">
            Maximum file size: 5 megabytes. For best results, maintain a 3.5:1
            aspect ratio. Accepted formats: PNG, JPG, or JPEG.
          </p>
          <p className="text-graySlate mb-[24px] text-center">
            Looking for a header image? Get a free stock photo here{" "}
            <a
              href="https://pixabay.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blurple"
            >
              https://pixabay.com
            </a>
          </p>
        </div>
        {showCrop ? (
          <div className="mb-[48px] flex w-full flex-col gap-6">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              aspect={3.5 / 1}
              className="min-h-[60px] min-w-[60px]"
            >
              {previewImgUrl ? (
                <img
                  ref={setImgRef}
                  src={previewImgUrl}
                  alt="banner preview"
                  onLoad={onImageLoad}
                  className="max-h-[400px] w-full object-contain"
                />
              ) : (
                <img
                  ref={setImgRef}
                  src={`${
                    coachPublicProfileData?.banner_image
                  }?timestamp=${new Date().getTime()}`}
                  alt="banner"
                  onLoad={onImageLoad}
                  className="max-h-[400px] w-full object-contain"
                />
              )}
            </ReactCrop>
            <div className="mx-auto flex gap-2">
              <Button onClick={() => setShowCrop(false)} variant="link">
                Cancel
              </Button>
              <Button onClick={handleCropComplete}>Apply Crop</Button>
            </div>
          </div>
        ) : (
          <div className="mb-[48px] h-[217px] w-full">
            {coachPublicProfileData?.has_banner_image ? (
              <>
                {awaitNewImage ? (
                  <DragDropArea
                    styling={` w-full h-full rounded-lg`}
                    mediaFile={bannerImgFile}
                    setMediaFile={setBannerImgFile}
                    mediaType="image"
                  />
                ) : (
                  <>
                    {previewImgUrl ? (
                      <img
                        src={previewImgUrl}
                        alt="preview"
                        className="h-full w-full rounded-lg object-cover"
                      />
                    ) : (
                      <img
                        src={`${
                          coachPublicProfileData?.banner_image
                        }?timestamp=${new Date().getTime()}`}
                        alt="banner"
                        className="h-full w-full rounded-lg object-cover"
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {previewImgUrl ? (
                  <img
                    src={previewImgUrl}
                    alt="preview"
                    className="h-full w-full rounded-lg object-cover"
                  />
                ) : (
                  <DragDropArea
                    styling={` w-full h-full rounded-lg`}
                    mediaFile={bannerImgFile}
                    setMediaFile={setBannerImgFile}
                    mediaType="image"
                  />
                )}
              </>
            )}
          </div>
        )}
        <div className="mx-auto flex flex-wrap items-center justify-center gap-[12px]">
          {loadingDelete ? (
            <Loader />
          ) : coachPublicProfileData?.has_banner_image ? (
            <Button variant={"utility"} onClick={deleteBannerImage}>
              <SvgTrash />
              Delete
            </Button>
          ) : (
            <></>
          )}
          {!showCrop && !awaitNewImage &&
          (coachPublicProfileData?.has_banner_image || previewImgUrl) ? (
            <>
              <Button
                variant="utility"
                onClick={() => {
                  setBannerImgFile(undefined)
                  setAwaitNewImage(true)
                  setImageProperties(null)
                  setPreviewImgUrl("")
                }}
              >
                <SvgUploadImage />
                Upload
              </Button>
              <Button
                variant="utility"
                onClick={() => {
                  setShowCrop(true)
                }}
              >
                <SvgCrop />
                Crop
              </Button>
            </>
          ) : (
            <></>
          )}
          {loadingSave ? (
            <Loader />
          ) : (
            <Button onClick={saveBannerImage} disabled={!imageProperties}>
              Save Image
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
